import { Vector3, Euler, MathUtils } from 'three';

export interface PhysicsConfig {
  maxSpeed: number;       // km/h
  minSpeed: number;       // km/h
  boostMultiplier: number;
  acceleration: number;   // km/h per second
  deceleration: number;   // km/h per second
  pitchRate: number;      // rad/s per mouse unit
  rollRate: number;       // rad/s per mouse unit
  yawCoupling: number;    // roll → yaw coupling factor
  mouseSensitivity: number;
  autoLevelStrength: number; // 自動水平戻し強度
}

export const DEFAULT_PHYSICS: PhysicsConfig = {
  maxSpeed: 950,
  minSpeed: 150,
  boostMultiplier: 1.3,
  acceleration: 200,
  deceleration: 150,
  pitchRate: 1.5,
  rollRate: 2.0,
  yawCoupling: 0.4,
  mouseSensitivity: 0.002,
  autoLevelStrength: 0.6,
};

/** km/h → Three.js units/s (1 unit = 1 m) */
function kmhToUnitsPerSec(kmh: number): number {
  return (kmh * 1000) / 3600;
}

export class AircraftPhysics {
  speed = 300; // km/h
  pitch = 0;   // ラジアン（機体のオイラー角X）
  roll = 0;    // ラジアン（機体のオイラー角Z）
  yaw = 0;     // ラジアン（機体のオイラー角Y）

  private cfg: PhysicsConfig;

  constructor(config: PhysicsConfig = DEFAULT_PHYSICS) {
    this.cfg = config;
  }

  update(
    delta: number,
    mouseDx: number,
    mouseDy: number,
    accel: boolean,
    decel: boolean,
    boost: boolean
  ): void {
    const cfg = this.cfg;

    // --- 速度更新 ---
    const maxSpd = boost ? cfg.maxSpeed * cfg.boostMultiplier : cfg.maxSpeed;

    if (boost) {
      // アフターバーナー: W不要で一気に最高速へ（通常加速の3倍）
      this.speed = Math.min(this.speed + cfg.acceleration * 3 * delta, maxSpd);
    } else if (accel) {
      this.speed = Math.min(this.speed + cfg.acceleration * delta, maxSpd);
    } else if (decel) {
      this.speed = Math.max(this.speed - cfg.deceleration * delta, cfg.minSpeed);
    } else {
      // 徐々に中間速度に近づく（慣性）
      const target = cfg.maxSpeed * 0.5;
      this.speed += (target - this.speed) * 0.3 * delta;
      this.speed = MathUtils.clamp(this.speed, cfg.minSpeed, maxSpd);
    }

    // --- ピッチ/ロール（マウス入力）---
    const sens = cfg.mouseSensitivity;
    this.pitch += mouseDy * sens * cfg.pitchRate;
    this.roll  += mouseDx * sens * cfg.rollRate;

    // ピッチを -π〜π に正規化（フルループ可能）
    if (this.pitch > Math.PI) this.pitch -= Math.PI * 2;
    if (this.pitch < -Math.PI) this.pitch += Math.PI * 2;

    // ロールに連動したヨー
    this.yaw -= this.roll * cfg.yawCoupling * delta;

    // オートレベル（マウス無入力時にピッチ・ロールを水平に戻す）
    const noInput = Math.abs(mouseDx) < 0.01 && Math.abs(mouseDy) < 0.01;
    if (noInput) {
      // ピッチを0に戻す（水平飛行へ）
      const pitchLevel = cfg.autoLevelStrength * 0.4 * delta;
      if (Math.abs(this.pitch) < pitchLevel) {
        this.pitch = 0;
      } else {
        this.pitch -= Math.sign(this.pitch) * pitchLevel;
      }
    }

    // ロールのオートレベル
    this.roll *= (1 - cfg.autoLevelStrength * delta);
    if (Math.abs(this.roll) < 0.001) this.roll = 0;
  }

  /** 現在フレームの前進ベクトル（機体座標系、長さ=速度[units/s]）*/
  getVelocity(): Vector3 {
    const spd = kmhToUnitsPerSec(this.speed);
    // 機体が向いている方向に前進（オイラー角から計算）
    const v = new Vector3(0, 0, -spd); // ローカル前方 = -Z
    v.applyEuler(new Euler(this.pitch, this.yaw, this.roll, 'YXZ'));
    return v;
  }

  get speedKmh(): number { return this.speed; }
}
